import {
  LitElement,
  html
} from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import ProgressIndicatorStyle from './style.js';

export class LdsProgressIndicator extends LitElement {
  static get styles() {
    return ProgressIndicatorStyle;
  }

  static properties = {
    size: { type: Number },
    progress: { type: Number },
    ariaLabel: { type: String },
    ariaValueMin: { type: Number },
    ariaValueMax: { type: Number },
  };

  constructor() {
    super();
    this.size = 2;
    this.progress = 0;
    this.ariaLabel = 'Processing';
    this.ariaValueMin = 0;
    this.ariaValueMax = 100;
  }

  /**
   * Size should not be less than 1.5 or greater than 25
   */
  getSize() {
    let size = this.size;

    if (size < 1.5) {
      // eslint-disable-next-line no-console
      console.warn(`
      LDS Warning: A Progress Indicator size value was set to ${size} which is less than the minimum of 1.5.
      This restriction enforces visual readability and prevents useless excessive shrinking of the element.
      The size value will be overridden to the minimum of 1.5.
      Please set a valid size value to between 1.5 and 25.0 to clear this warning.
      `);
      size = 1.5;
    }

    if (size > 25) {
      // eslint-disable-next-line no-console
      console.warn(`
      LDS Warning: A Progress Indicator size value was set to ${size} which is greater than the maximum of 25.0.
      This restriction prevents useless excessive embiggening of the element.
      The size will be overridden to the maximum of 25.
      Please set a valid size value to between 1.5 and 25.0 to clear this warning.
      `);
      size = 25;
    }

    return size;
  }

  /**
   * Progress is a percentage of the circle shown as filled, therefore it should not be less than 0 or greater than 100
   */
  getProgress() {
    let progress = this.progress;

    if (progress < 0) {
      // eslint-disable-next-line no-console
      console.warn(`
      LDS Warning: A Progress Indicator progress percentage value was set to ${progress}% which is invalid.
      The progress percent will be overridden to the minimum of 0.
      To correctly customize the value, please set it between 0-100 inclusive to clear this warning.
      `);
      progress = 0;
    }

    if (progress > 100) {
      // eslint-disable-next-line no-console
      console.warn(`
      LDS Warning: A Progress Indicator progress percentage value was set to ${progress}% which is invalid.
      The progress percent will be overridden to the maximum of 100.
      To correctly customize the value, please set it between 0-100 inclusive to clear this warning.
      `);
      progress = 100;
    }

    return progress;
  }

  getAriaLabel() {
    let ariaLabel = this.ariaLabel;

    if (!ariaLabel) {
      // eslint-disable-next-line no-console
      console.warn(`
      LDS Warning: A Progress Indicator has an ariaLabel value that is empty, undefined or otherwise "falsy"; a valid string value for ariaLabel is required on elements with role="progressbar" such as this one. The default value of "Processing" will be used.
      input ariaLabel: ${this.ariaLabel}
      To correctly customize the value, please input a valid string to clear this warning.
      `);
      ariaLabel = 'Processing';
    }

    return ariaLabel;
  }

  getAriaValueMin() {
    let valueMin = this.ariaValueMin;

    if (valueMin !== 0) {
      let warn = false;
      if (valueMin < 0) {
        valueMin = 0;
        warn = true;
      }
      if (valueMin > 100) {
        valueMin = 100;
        warn = true;
      }

      if (warn) {
        // eslint-disable-next-line no-console
        console.warn(`
        LDS Warning: A Progress Indicator has the ariaValueMin value set incorrectly; the value was either greater than 100 or less than 0 (zero).
        input ariaValueMin: ${this.ariaValueMin}
        The ariaValueMin will be rendered as: ${valueMin}
        To correctly customize the value, please set it between 0-100 inclusive to clear this warning.
        `);
      }
    }

    return valueMin;
  }

  getAriaValueMax() {
    let valueMax = this.ariaValueMax;

    if (valueMax !== 100) {
      let warn = false;
      if (valueMax < 0) {
        valueMax = 0;
        warn = true;
      }
      if (valueMax > 100) {
        valueMax = 100;
        warn = true;
      }

      if (warn) {
        // eslint-disable-next-line no-console
        console.warn(`
        LDS Warning: A Progress Indicator has the ariaValueMax value set incorrectly; the value was either greater than 100 or less than 0 (zero).
        input ariaValueMin: ${this.ariaValueMax}
        The ariaValueMax will be rendered as: ${valueMax}
        To customize the value, please set it between 0-100 inclusive to clear this warning.
        `);
      }
    }

    return valueMax;
  }

  getComponentClasses() {
    let componentClass = 'lds-progress-indicator';

    if (this.progress > 0 && this.progress < 100) {
      if (this.progress < 10) return `${componentClass} progress-incomplete progress-0`;
      if (this.progress < 20) return `${componentClass} progress-incomplete progress-10`;
      if (this.progress < 30) return `${componentClass} progress-incomplete progress-20`;
      if (this.progress < 40) return `${componentClass} progress-incomplete progress-30`;
      if (this.progress < 50) return `${componentClass} progress-incomplete progress-40`;
      if (this.progress < 60) return `${componentClass} progress-incomplete progress-50`;
      if (this.progress < 70) return `${componentClass} progress-incomplete progress-60`;
      if (this.progress < 80) return `${componentClass} progress-incomplete progress-70`;
      if (this.progress < 90) return `${componentClass} progress-incomplete progress-80`;
      if (this.progress < 100) return `${componentClass} progress-incomplete progress-90`;
    }

    if (this.progress >= 100) return `${componentClass} progress-complete`;

    return componentClass;
  }

  render() {
    // Get the input properties to use
    const progress = this.getProgress();
    const size = this.getSize();
    let ariaLabel = this.getAriaLabel();
    let ariaValueMin = this.getAriaValueMin();
    let ariaValueMax = this.getAriaValueMax();

    // Circle dimension calculations
    const radius = size * 20;
    const stroke = size * 2;
    const innerStroke = stroke / 2;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;

    // Stroke dash offset for the generated SVG circle.
    const strokeDashOffset = circumference - ((progress / 100) * circumference);
    const strokeDashOffsetStyle = {
      strokeDashoffset: `${strokeDashOffset}`,
    };

    // Diameter for the entire SVG circle shape. Also the width and height of the box it will take up.
    const diameter = radius * 2;
    const boxSizeStyle = {
      height: `${diameter}px`,
      width: `${diameter}px`,
    };

    // Size of the percent text is proportional to size
    const fontSize = size * 7;
    const fontSizeStyle = {
      fontSize: `${fontSize}px`,
    };

    // The ARIA max/min values must be logical, fall back to the defaults if not
    if ((ariaValueMin > ariaValueMax) || (ariaValueMax < ariaValueMin)) {
      // eslint-disable-next-line no-console
      console.warn(`
      LDS Warning: A Progress Indicator has illogical ariaValueMin or ariaValueMax values set. Min must be less than max; max must be less than min. To avoid an illogical condition, the default of min = 0 and max = 100 will be used.
      Input ariaValueMin: ${this.ariaValueMin}
      Input ariaValueMax: ${this.ariaValueMax}
      Please fix the illogical min/max values above when customizing these ARIA attributes to resolve this warning.
      `);
      ariaValueMin = 0;
      ariaValueMax = 100;
    }

    return html`
      <div
        role="progressbar"
        class="${this.getComponentClasses()}"
        style="${styleMap(boxSizeStyle)}"
        aria-label="${ariaLabel}"
        aria-valuemin="${ariaValueMin}"
        aria-valuemax="${ariaValueMax}"
        aria-valuenow="${progress}"
      >
        <span
          class="lds-progress-indicator-text"
          style="${styleMap(fontSizeStyle)}"
        >
          ${progress}%
        </span>
        <svg
          height="${diameter}"
          width="${diameter}"
          viewBox="0 0 ${diameter} ${diameter}"
        >
          <circle
            class="lds-progress-indicator-circle-base"
            r="${normalizedRadius}"
            cx="${radius}"
            cy="${radius}"
            stroke-width="${innerStroke}"
          />
          <circle
            class="lds-progress-indicator-circle-progress"
            r="${normalizedRadius}"
            cx="${radius}"
            cy="${radius}"
            style="${styleMap(strokeDashOffsetStyle)}"
            stroke-dasharray="${circumference} ${circumference}"
            stroke-width="${stroke}"
            stroke-linecap="round"
          />
        </svg>
      </div>
    `;
  }
}

export function defineComponent() {
  if (!window.customElements.get('lds-progress-indicator')) {
    window.customElements.define('lds-progress-indicator', LdsProgressIndicator);
  }
}

export default { LdsProgressIndicator, defineComponent };
